/** Maximum pixel size (px) for the longest edge after downscaling. 1920 px matches full-HD width. */
const MAX_DIMENSION = 1920;
/** Maximum allowed file size in bytes (5 MB) — matches the server-side limit in download-product-image and ImageUpload. */
const MAX_FILE_SIZE = 5 * 1024 * 1024;
/** JPEG quality values tried in order; the loop stops at the first one that produces a file under MAX_FILE_SIZE. */
const QUALITY_STEPS = [0.8, 0.6, 0.4];

/**
 * Compresses an image file using the Canvas API for upload optimisation.
 *
 * Processing steps:
 * 1. SVGs are returned untouched (non-raster format).
 * 2. Downscales to a maximum of 1920 px on the longest edge.
 * 3. Encodes as progressive JPEG, iterating quality levels (0.8, 0.6, 0.4)
 *    until the output is under 5 MB.
 * 4. Returns the original file if compression increases size (e.g. small PNGs).
 *
 * The output filename is renamed to `.jpg` to match the JPEG content type.
 *
 * @param file - The source image `File` to compress.
 * @returns A `File` containing the compressed JPEG, or the original if
 *   compression would not reduce size or the input is SVG.
 */
export async function compressImage(file: File): Promise<File> {
  // Skip non-raster formats
  if (file.type === 'image/svg+xml') return file;

  const bitmap = await createImageBitmap(file);
  const { width, height } = bitmap;

  // Calculate scaled dimensions
  let newWidth = width;
  let newHeight = height;
  if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
    const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
    newWidth = Math.round(width * ratio);
    newHeight = Math.round(height * ratio);
  }

  // Draw to canvas
  const canvas = new OffscreenCanvas(newWidth, newHeight);
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(bitmap, 0, 0, newWidth, newHeight);
  bitmap.close();

  // Try progressively lower quality until the file is under MAX_FILE_SIZE.
  // `bestBlob` always holds the last attempted blob — if even 40% quality
  // exceeds the limit, `bestBlob` will be the 40% result rather than null,
  // so the caller (ImageUpload.tsx) can check the size and show an error.
  let bestBlob: Blob | null = null;
  for (const quality of QUALITY_STEPS) {
    const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality });
    bestBlob = blob;
    if (blob.size <= MAX_FILE_SIZE) break;
  }

  // Guard: `bestBlob` is only null if QUALITY_STEPS is empty, which never
  // happens in practice, but TypeScript requires the check.
  if (!bestBlob) return file;

  // Return the original if compression increased the file size (e.g. a small
  // PNG that encodes more efficiently than JPEG at the same resolution).
  if (bestBlob.size >= file.size) return file;

  // Always output as .jpg so the file extension matches the JPEG content type.
  return new File([bestBlob], file.name.replace(/\.[^.]+$/, '.jpg'), {
    type: 'image/jpeg',
  });
}
